import React, { useContext } from 'react';
import {
  MetricasResumenPublicContext } from '../context/MetricasResumenPublicContext.jsx';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Stat,
  Progress,
  Table,
} from '@chakra-ui/react';
import { Thead, Tbody, Tr, Th, Td } from '@/components/ui/chakra-v2-compat';
import { motion } from 'framer-motion';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  LabelList,
} from 'recharts';

export const MotionBox = motion.create(Box);

export const LABEL_ESTADO_ENSAYO = {
  PROGRAMADO: 'Programados',
  CANCELADO: 'Cancelados',
  REALIZADO: 'Realizados',
};
export const LABEL_ESTADO_ASIS = {
  PENDIENTE: 'Pendiente',
  PRESENTE: 'Presente',
  AUSENTE: 'Ausente',
  JUSTIFICADO: 'Justificado',
};
export const LABEL_ESTADO_ACTUACION = {
  BORRADOR: 'Borrador',
  ENCUESTA_ABIERTA: 'Encuesta abierta',
  EN_PREPARACION: 'En preparación',
  LISTA: 'Lista',
  ARCHIVADA: 'Archivada',
};

export const LABEL_AVAL_STATUS = {
  PENDING: 'Pendiente de aceptar',
  ACCEPTED: 'Aceptado',
  REJECTED: 'Rechazado',
};

export const COL_ASIS = {
  PENDIENTE: 'gray.300',
  PRESENTE: 'green.500',
  AUSENTE: 'red.400',
  JUSTIFICADO: 'orange.400',
};

export function MiniDistribucionAsist({ porEstado }) {
  const n = (k) => porEstado?.[k] ?? 0;
  const entries = [
    ['PENDIENTE', n('PENDIENTE'), COL_ASIS.PENDIENTE],
    ['PRESENTE', n('PRESENTE'), COL_ASIS.PRESENTE],
    ['AUSENTE', n('AUSENTE'), COL_ASIS.AUSENTE],
    ['JUSTIFICADO', n('JUSTIFICADO'), COL_ASIS.JUSTIFICADO],
  ];
  const total = entries.reduce((s, [, v]) => s + v, 0);
  if (total === 0) return <Text fontSize="sm" color="gray.500">Sin registros</Text>;
  return (
    <HStack h="10px" borderRadius="md" overflow="hidden" w="100%" maxW="280px" bg="blackAlpha.100" gap={0}>
      {entries.map(([k, v, bg]) =>
        v > 0 ? (
          <Box key={k} h="100%" w={`${(v / total) * 100}%`} bg={bg} title={`${k}: ${v}`} />
        ) : null
      )}
    </HStack>
  );
}

export function formatPct(f) {
  if (f == null || Number.isNaN(f)) return '—';
  return `${(f * 100).toFixed(1)}%`;
}

export function nombreMusico(n, a) {
  return [n, a].filter(Boolean).join(' ').trim() || '—';
}

export const LABEL_DELTA_KPI = {
  ensayosTotal: 'Ensayos (total)',
  horasSuma: 'Horas de ensayo (con hora de fin)',
  actuacionesTotal: 'Actuaciones (con fecha)',
  presenteRegistros: 'Asist. ensayo «Presente»',
  obrasCreadas: 'Obras creadas (archivo)',
  paginasCreadas: 'Páginas creadas (archivo)',
  resenasRecibidas: 'Reseñas recibidas (fecha de creación)',
};

export function formatDeltaLine(d) {
  if (!d) return '—';
  const sign = d.diff > 0 ? '+' : '';
  const rel =
    d.diffRel != null && !Number.isNaN(d.diffRel)
      ? ` (${sign}${(d.diffRel * 100).toFixed(1)}% vs ant.)`
      : '';
  return `${sign}${d.diff}${rel}`;
}

const PIE_NEUTRAL = [
  '#805AD5',
  '#2C7A7B',
  '#3182CE',
  '#D69E2E',
  '#E53E3E',
  '#4FD1C5',
  '#9F7AEA',
  '#ED8936',
];

export const HEX_EST_ENSAYO = { PROGRAMADO: '#805AD5', CANCELADO: '#E53E3E', REALIZADO: '#38A169' };
export const HEX_EST_ASIS = {
  PENDIENTE: '#A0AEC0',
  PRESENTE: '#38A169',
  AUSENTE: '#F56565',
  JUSTIFICADO: '#ED8936',
};
export const HEX_EST_ACT = {
  BORRADOR: '#718096',
  ENCUESTA_ABIERTA: '#4FD1C5',
  EN_PREPARACION: '#B794F4',
  LISTA: '#2B6CB0',
  ARCHIVADA: '#805AD5',
};

export const BORDE_EST_ENSAYO = {
  PROGRAMADO: 'purple.400',
  CANCELADO: 'red.400',
  REALIZADO: 'green.400',
};
export const BORDE_EST_ACT = {
  BORRADOR: 'gray.400',
  ENCUESTA_ABIERTA: 'cyan.400',
  EN_PREPARACION: 'purple.300',
  LISTA: 'blue.400',
  ARCHIVADA: 'purple.600',
};

export const HEX_AVAL = { PENDING: '#D69E2E', ACCEPTED: '#38A169', REJECTED: '#E53E3E' };

export function toPieData(record, labelMap) {
  return Object.entries(record || {})
    .filter(([, n]) => n > 0)
    .map(([k, v], i) => ({
      name: labelMap[k] || k,
      value: v,
      key: k,
      color: PIE_NEUTRAL[i % PIE_NEUTRAL.length],
    }));
}

export function toPieDataWithKeyColors(record, labelMap, hexByKey) {
  return Object.entries(record || {})
    .filter(([, n]) => n > 0)
    .map(([k, v], i) => ({
      name: labelMap[k] || k,
      value: v,
      key: k,
      color: hexByKey[k] || PIE_NEUTRAL[i % PIE_NEUTRAL.length],
    }));
}

export function RenderPie({ data, height = 200 }) {
  if (!data.length) {
    return (
      <Text fontSize="sm" color="gray.500">
        Sin datos
      </Text>
    );
  }
  return (
    <Box w="100%" h={`${height}px`}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={48}
            outerRadius={height > 200 ? 88 : 72}
            paddingAngle={2}
            animationDuration={600}
            animationEasing="ease-out"
          >
            {data.map((entry) => (
              <Cell key={entry.key} fill={entry.color} stroke="rgba(0,0,0,0.06)" />
            ))}
          </Pie>
          <RechartsTooltip
            formatter={(v) => [v, '']}
            contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0' }}
          />
          <Legend layout="horizontal" verticalAlign="bottom" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
}

export function barDataFromObras(obras) {
  return (obras || []).slice(0, 10).map((o) => ({
    name: o.titulo && o.titulo.length > 36 ? `${o.titulo.slice(0, 34)}…` : o.titulo || '—',
    value: o.apariciones,
  }));
}

export function barDataFromAutores(autores) {
  return (autores || []).slice(0, 10).map((a) => ({
    name: a.autor && a.autor.length > 36 ? `${a.autor.slice(0, 34)}…` : a.autor || '—',
    value: a.apariciones,
  }));
}

export function barDataSedesGrupos(items) {
  return (items || []).map((x) => ({
    name: (x.nombre || '—').length > 34 ? `${(x.nombre || '—').slice(0, 32)}…` : x.nombre || '—',
    value: x.ensayos,
  }));
}

export function BarHorizMini({ data, h = 200 }) {
  const resumenPublic = useContext(MetricasResumenPublicContext);
  if (!data.length) return <Text fontSize="sm" color="gray.500">Sin datos</Text>;
  /** Eje X debe anclar en 0; si el dominio fuera [dataMin, dataMax] las barras casi no se alargan. */
  const tickFill = resumenPublic ? '#E2E8F0' : '#4A5568';
  const labelFill = resumenPublic ? 'rgba(255,255,255,0.92)' : '#2D3748';
  return (
    <Box w="100%" minW={0} h={`${h}px`}>
      <ResponsiveContainer width="100%" height="100%" minWidth={200}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: resumenPublic ? 36 : 12, left: 4, bottom: 4 }}>
          <XAxis type="number" domain={[0, 'dataMax']} hide />
          <YAxis
            dataKey="name"
            type="category"
            width={118}
            tick={{ fontSize: 10, fill: tickFill }}
            interval={0}
            axisLine={false}
            tickLine={false}
          />
          <RechartsTooltip
            contentStyle={{ borderRadius: 12 }}
            labelStyle={{ color: labelFill }}
          />
          <Bar dataKey="value" name="N" radius={[0, 4, 4, 0]} fill="#805AD5" maxBarSize={24} isAnimationActive>
            <LabelList dataKey="value" position="right" fill={labelFill} fontSize={11} offset={6} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

export function ListDistribAnimada({ record, labelMap, colorOfKey, muted }) {
  const resumenPublic = useContext(MetricasResumenPublicContext);
  const entries = Object.entries(record || {}).filter(([, n]) => n > 0);
  if (entries.length === 0) {
    return (
      <Text fontSize="sm" color={muted}>
        —
      </Text>
    );
  }
  return (
    <VStack align="stretch" gap={2} mt={2}>
      {entries.map(([k, v], i) => {
        const bgDot = colorOfKey(k);
        return (
          <MotionBox
            key={k}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.04 * i, duration: 0.3 }}
          >
            <Flex
              align="center"
              py={1.5}
              px={3}
              borderRadius="lg"
              bg="blackAlpha.50"
              _dark={{ bg: 'whiteAlpha.100' }}
              borderLeft="4px solid"
              borderLeftColor={bgDot}
            >
              <Text flex="1" fontSize="sm" fontWeight="600" lineClamp={2}>
                {labelMap[k] || k}
              </Text>
              <Text
                fontSize="md"
                fontWeight="800"
                color={resumenPublic ? 'whiteAlpha.900' : 'gray.800'}
                _dark={resumenPublic ? undefined : { color: 'gray.100' }}
              >
                {v}
              </Text>
            </Flex>
          </MotionBox>
        );
      })}
    </VStack>
  );
}

export function SeccionCaja({
  titulo,
  tituloColor,
  barraAccento = true,
  descripcion,
  muted,
  children,
  gridColumn,
  ...boxProps
}) {
  const resumenPublic = useContext(MetricasResumenPublicContext);
  return (
    <Box
      gridColumn={gridColumn}
      borderWidth="1px"
      borderRadius="2xl"
      p={4}
      position="relative"
      overflow="hidden"
      borderColor="whiteAlpha.200"
      bg={resumenPublic ? 'rgba(255,255,255,0.06)' : undefined}
      _light={resumenPublic ? undefined : { borderColor: 'gray.200', bg: 'white' }}
      _dark={resumenPublic ? undefined : { bg: 'whiteAlpha.50' }}
      boxShadow="0 1px 3px rgba(0,0,0,0.06)"
      {...boxProps}
    >
      {barraAccento && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          h="3px"
          bgGradient="linear(to-r, purple.400, cyan.300)"
        />
      )}
      <Text fontSize="sm" fontWeight="800" color={tituloColor} letterSpacing="0.02em" mb={descripcion ? 1 : 3}>
        {titulo}
      </Text>
      {descripcion ? (
        <Text fontSize="xs" color={muted} mb={3}>
          {descripcion}
        </Text>
      ) : null}
      {children}
    </Box>
  );
}
